/**
 * Native replacements for Lodash object utilities
 */

/**
 * Creates a deep clone of a value using structuredClone (native browser API)
 * Falls back to a recursive clone for older environments or unsupported types (functions, DOM nodes).
 * Functions are returned by reference (matching lodash behavior).
 */
export function cloneDeep<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }

  // Functions and primitives are returned by reference, matching lodash behavior
  if (typeof value !== 'object') {
    return value;
  }

  // Try structuredClone first if available, but catch errors for unsupported types
  // (functions as values, DOM nodes, some class instances throw DataCloneError)
  if (typeof structuredClone !== 'undefined') {
    try {
      return structuredClone(value);
    } catch {
      // Fall through to recursive clone
    }
  }

  // Fallback: recursive clone that preserves function references (unlike JSON.parse/stringify)
  if (Array.isArray(value)) {
    return value.map(cloneDeep) as T;
  }

  const result = {} as Record<string, unknown>;
  for (const key of Object.keys(value as object)) {
    result[key] = cloneDeep((value as Record<string, unknown>)[key]);
  }
  return result as T;
}

/**
 * Creates an object with specified keys omitted
 * @example omit({ a: 1, b: 2, c: 3 }, ['a', 'c']) // { b: 2 }
 */
// biome-ignore lint/suspicious/noExplicitAny: Need flexible object type for omit utility
export function omit<T extends Record<string, any>, K extends PropertyKey>(
  obj: T,
  keys: K[] | readonly K[],
): Omit<T, K> {
  const result = { ...obj };
  for (const key of keys) {
    if (key in result) {
      delete result[key as keyof typeof result];
    }
  }
  return result as Omit<T, K>;
}

/**
 * Performs a deep equality check between two values
 * Handles NaN correctly (NaN is equal to NaN)
 * Supports Date, RegExp, and plain objects/arrays
 */
export function isEqual(a: unknown, b: unknown): boolean {
  // Object.is handles NaN correctly (Object.is(NaN, NaN) === true)
  if (Object.is(a, b)) return true;
  if (a === null || b === null) return false;
  if (a === undefined || b === undefined) return false;

  if (typeof a !== 'object' || typeof b !== 'object') {
    return false; // Already checked with Object.is above
  }

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }
  if (a instanceof Date || b instanceof Date) {
    return false;
  }

  // Handle RegExp objects
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.source === b.source && a.flags === b.flags;
  }
  if (a instanceof RegExp || b instanceof RegExp) {
    return false;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  // Verify both objects have the same keys
  return keysA.every(key => {
    // Check that b has this key
    if (!(key in (b as Record<string, unknown>))) {
      return false;
    }
    const valueA = (a as Record<string, unknown>)[key];
    const valueB = (b as Record<string, unknown>)[key];
    return isEqual(valueA, valueB);
  });
}

/**
 * Checks if a value is null or undefined
 */
export function isNil(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}
